// Firestore-backed data layer.  Once a user has been migrated (see
// firestoreMigration.js), the app reads and writes their matches, teams and
// tournaments here instead of the legacy VBLive API.  The legacy wrappers in
// dbutils.js / VBLiveAPIAction.js delegate to these functions when
// `usingFirestore()` is true and otherwise fall back to the old API, so the
// rest of the app keeps using the same function signatures and data shapes.

import { getAuth } from "firebase/auth";
import {
    arrayUnion,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    limit,
    query,
    serverTimestamp,
    setDoc,
    updateDoc,
    where,
} from "firebase/firestore";
import { db } from "../../firebase.config";
import {
    compressCatapultFocusData,
    decompressCatapultFocusData,
} from "./CatapultFocusFile";
import { generateMatch } from "./DVWFile";
import { isMigratedCached } from "./firestoreMigration";
import { initWithPSVBCompressedBuffer } from "./PSVBFile";
import { collectTeamsAndPlayers } from "./teamsPlayers";
import { myzip } from "./zip";

function currentUid() {
  return getAuth().currentUser?.uid ?? null;
}

function currentEmail() {
  return getAuth().currentUser?.email ?? null;
}

/** True when the signed-in user's data lives in Firestore. */
export function usingFirestore() {
  return isMigratedCached(currentUid());
}

function slugify(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120);
}

function safeId(value) {
  return String(value ?? "").replace(/\//g, "_");
}

// Normalise an email for use as part of a Firestore document ID.
function safeEmailId(email) {
  return String(email ?? "").toLowerCase().replace(/[^a-z0-9]/g, "_");
}

function parseMaybeJSON(value) {
  if (value == null) return null;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

// Map a Firestore `matches` document to the legacy match-metadata shape the UI
// already expects (id + decoded team/tournament names + dates + video/share).
function matchFromDoc(snap) {
  const d = snap.data() ?? {};
  return {
    id: snap.id,
    teamA: d.teamA ?? "",
    teamB: d.teamB ?? "",
    description: d.description ?? "",
    tournament: d.tournament ?? "",
    scores: d.scores ?? "",
    origin: d.origin ?? null,
    sessionDateTimeInSeconds: d.sessionDateTimeInSeconds ?? null,
    sessionDateString: d.sessionDateString ?? "",
    videoOnlineUrl: d.videoOnlineUrl ?? null,
    videoStartTimeSeconds: d.videoStartTimeSeconds ?? null,
    videoOffset: d.videoOffset ?? null,
    shareStatus: d.shareStatus ?? null,
    shareUsers: d.shareUsers ?? null,
    appName: d.appName ?? "VBLive",
    // "Points to close a set" override chosen on import (Input.jsx)
    // — re-applied whenever this match's DVW buffer is re-parsed so the
    // sets-won match score stays consistent across reloads.
    winScore: d.winScore ?? null,
    winByTwo: d.winByTwo ?? null,
    // When this match's shareStatus/shareUsers were last (re)written by
    // fsShareSession — lets a recipient's earlier dismissal (see
    // fsRemoveSharedMatchForRecipient) be superseded by a LATER re-share.
    lastSharedAt: d.lastSharedAt?.toDate?.() ?? null,
  };
}

// ---- Reads ----------------------------------------------------------------

// ---- View tracking --------------------------------------------------------

// Records that viewerEmail opened the given match. Uses a 5-minute dedup
// bucket so rapidly re-opening the same match doesn't stack up records.
export async function fsRecordMatchView(matchId, viewerEmail) {
  if (!matchId || !viewerEmail) return;
  const id = safeId(matchId);
  const bucket = Math.floor(Date.now() / (5 * 60 * 1000));
  const viewDocId = `${id}_${safeEmailId(viewerEmail)}_${bucket}`;
  try {
    await setDoc(doc(db, "matchViews", viewDocId), {
      matchId: id,
      viewerEmail,
      viewedAt: serverTimestamp(),
    }, { merge: true });
  } catch (e) {
    console.warn("fsRecordMatchView:", e?.code ?? e);
  }
}

// Returns up to 100 view records for a match, newest first.
export async function fsFetchMatchViews(matchId) {
  if (!matchId) return [];
  const id = safeId(matchId);
  try {
    const snap = await getDocs(
      query(
        collection(db, "matchViews"),
        where("matchId", "==", id),
        limit(100)
      )
    );
    return snap.docs
      .map((d) => {
        const data = d.data();
        return {
          id: d.id,
          viewerEmail: data.viewerEmail ?? "",
          viewedAt: data.viewedAt?.toDate?.() ?? null,
        };
      })
      .sort((a, b) => (b.viewedAt ?? 0) - (a.viewedAt ?? 0));
  } catch (e) {
    console.warn("fsFetchMatchViews:", e?.code ?? e);
    return [];
  }
}

export async function fsFetchMatchViewsForMatches(matchIds) {
  if (!matchIds || matchIds.length === 0) return [];
  const safeIds = [...new Set(matchIds.map(safeId))];
  const results = [];
  const CHUNK = 30;
  for (let i = 0; i < safeIds.length; i += CHUNK) {
    const chunk = safeIds.slice(i, i + CHUNK);
    try {
      const snap = await getDocs(
        query(collection(db, "matchViews"), where("matchId", "in", chunk), limit(500))
      );
      for (const d of snap.docs) {
        const data = d.data();
        results.push({
          id: d.id,
          matchId: data.matchId ?? "",
          viewerEmail: data.viewerEmail ?? "",
          viewedAt: data.viewedAt?.toDate?.() ?? null,
        });
      }
    } catch (e) {
      console.warn("fsFetchMatchViewsForMatches chunk failed:", e?.code ?? e);
    }
  }
  results.sort((a, b) => (b.viewedAt ?? 0) - (a.viewedAt ?? 0));
  return results;
}

export async function fsFetchAllMyMatches() {
  const uid = currentUid();
  if (!uid) return [];
  const q = query(collection(db, "matches"), where("owner", "==", uid));
  const snap = await getDocs(q);
  const matches = snap.docs.map(matchFromDoc);
  // Newest first, matching the legacy list ordering used by MatchesList.
  matches.sort(
    (a, b) =>
      (b.sessionDateTimeInSeconds ?? 0) - (a.sessionDateTimeInSeconds ?? 0)
  );
  return matches;
}

// Build a sharedGrant metadata object from a match row.
function grantMeta(match) {
  return {
    teamA: match.teamA ?? "",
    teamB: match.teamB ?? "",
    description: match.description ?? "",
    tournament: match.tournament ?? "",
    scores: match.scores ?? "",
    origin: match.origin ?? null,
    sessionDateTimeInSeconds: match.sessionDateTimeInSeconds ?? null,
    sessionDateString: match.sessionDateString ?? "",
    videoOnlineUrl: match.videoOnlineUrl ?? null,
    videoStartTimeSeconds: match.videoStartTimeSeconds ?? null,
    videoOffset: match.videoOffset ?? null,
    shareStatus: match.shareStatus ?? null,
    winScore: match.winScore ?? null,
    winByTwo: match.winByTwo ?? null,
  };
}

// Convert a sharedGrants document back to a match-metadata row.
function matchFromGrant(d, matchId) {
  return {
    id: matchId,
    teamA: d.teamA ?? "",
    teamB: d.teamB ?? "",
    description: d.description ?? "",
    tournament: d.tournament ?? "",
    scores: d.scores ?? "",
    origin: d.origin ?? null,
    sessionDateTimeInSeconds: d.sessionDateTimeInSeconds ?? null,
    sessionDateString: d.sessionDateString ?? "",
    videoOnlineUrl: d.videoOnlineUrl ?? null,
    videoStartTimeSeconds: d.videoStartTimeSeconds ?? null,
    videoOffset: d.videoOffset ?? null,
    shareStatus: d.shareStatus ?? null,
    shareUsers: null,
    winScore: d.winScore ?? null,
    winByTwo: d.winByTwo ?? null,
    ownerEmail: d.ownerEmail ?? "",
    sharedAt: d.sharedAt?.toDate?.() ?? null,
    appName: "VBLive",
  };
}

export async function fsFetchAllSharedMatches() {
  const email = currentEmail();
  if (!email) return [];
  const emailLower = email.toLowerCase();

  const matches = [];
  const seen = new Set();

  // Primary: sharedGrants collection — works without cross-user security rules.
  // Required Firestore rule:
  //   match /sharedGrants/{id} {
  //     allow read: if request.auth != null
  //       && request.auth.token.email.lower() == resource.data.recipientEmail;
  //     allow write: if request.auth != null
  //       && request.resource.data.ownerUid == request.auth.uid;
  //     allow delete: if request.auth != null
  //       && (resource.data.ownerUid == request.auth.uid
  //         || request.auth.token.email.lower() == resource.data.recipientEmail);
  //   }
  try {
    const q = query(
      collection(db, "sharedGrants"),
      where("recipientEmail", "==", emailLower)
    );
    const snap = await getDocs(q);
    for (const d of snap.docs) {
      const g = d.data();
      const mid = g.matchId;
      if (mid && !seen.has(mid)) {
        seen.add(mid);
        matches.push(matchFromGrant(g, mid));
      }
    }
  } catch (e) {
    console.error("fsFetchAllSharedMatches (sharedGrants) failed:", e);
  }

  // Fallback: direct matches collection queries.
  // Works only if Firestore rules allow reads where shareUsers contains the email.
  try {
    const q1 = query(
      collection(db, "matches"),
      where("shareUsers", "array-contains", emailLower)
    );
    const q2 = query(
      collection(db, "matches"),
      where("shareUsers", "==", emailLower)
    );
    const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
    for (const snap of [snap1, snap2]) {
      for (const d of snap.docs) {
        if (!seen.has(d.id)) {
          seen.add(d.id);
          matches.push(matchFromDoc(d));
        }
      }
    }
  } catch (e) {
    // Silently ignore — likely a security-rule denial on cross-user reads.
    console.warn("fsFetchAllSharedMatches (direct query) blocked:", e?.code ?? e);
  }

  // A recipient removing a shared match (fsRemoveSharedMatchForRecipient)
  // can't reliably stop it reappearing via the TWO sources above: deleting
  // the sharedGrants doc only removes it from the PRIMARY source, but the
  // FALLBACK direct query matches the OWNER's matches/{id}.shareUsers array
  // directly -- which the recipient never has permission to edit, and which
  // keeps listing them regardless of their own grant. So filter out
  // anything the recipient has explicitly dismissed, independent of which
  // source(s) surfaced it -- UNLESS the owner has since RE-SHARED the match
  // (a grant's own sharedAt, or the match doc's lastSharedAt, is newer than
  // the dismissal) -- otherwise a fresh re-share would stay invisible to
  // this recipient forever, since the dismissal record never expires on
  // its own.
  try {
    const dismissedSnap = await getDocs(
      query(
        collection(db, "sharedMatchDismissals"),
        where("recipientEmail", "==", emailLower)
      )
    );
    const dismissedAtByMatchId = new Map(
      dismissedSnap.docs.map((d) => [d.data().matchId, d.data().dismissedAt?.toDate?.() ?? null])
    );
    if (dismissedAtByMatchId.size > 0) {
      matches.splice(
        0,
        matches.length,
        ...matches.filter((m) => {
          if (!dismissedAtByMatchId.has(m.id)) return true;
          const dismissedAt = dismissedAtByMatchId.get(m.id);
          const sharedAt = m.sharedAt ?? m.lastSharedAt ?? null;
          return sharedAt != null && dismissedAt != null && sharedAt.getTime() > dismissedAt.getTime();
        })
      );
    }
  } catch (e) {
    console.warn("fsFetchAllSharedMatches (dismissals) failed:", e?.code ?? e);
  }

  matches.sort(
    (a, b) =>
      (b.sessionDateTimeInSeconds ?? 0) - (a.sessionDateTimeInSeconds ?? 0)
  );
  return matches;
}

// Removes a match from the CURRENT USER's own "Shared Matches" list only.
// Writes a sharedMatchDismissals/{matchId}_{email} doc -- keyed and owned
// entirely by the CURRENT user's own email, so it needs no special
// cross-user security rule at all (unlike deleting someone else's grant):
//   match /sharedMatchDismissals/{id} {
//     allow read: if request.auth != null
//       && request.auth.token.email.lower() == resource.data.recipientEmail;
//     allow create: if request.auth != null
//       && request.auth.token.email.lower() == request.resource.data.recipientEmail;
//     allow delete: if request.auth != null
//       && request.auth.token.email.lower() == resource.data.recipientEmail;
//   }
// fsFetchAllSharedMatches reads this per-recipient list and filters out any
// match it names, regardless of which source (sharedGrants or the direct
// matches-collection fallback) would otherwise have surfaced it -- this is
// what actually makes removal stick even when the owner's match doc still
// lists the recipient in shareUsers (which the recipient can never edit).
// Also best-effort deletes the recipient's own sharedGrants doc (keeps that
// collection from growing forever with dismissed shares); failure there
// (e.g. the owner-only delete rule hasn't been relaxed) doesn't matter --
// the dismissal record above is what actually hides the match either way.
export async function fsRemoveSharedMatchForRecipient(matchId) {
  const email = currentEmail();
  if (!email) return false;
  const emailLower = email.toLowerCase();
  const id = safeId(matchId);
  const grantId = `${id}_${safeEmailId(emailLower)}`;
  try {
    await setDoc(doc(db, "sharedMatchDismissals", grantId), {
      matchId: id,
      recipientEmail: emailLower,
      dismissedAt: serverTimestamp(),
    });
  } catch (e) {
    console.error("fsRemoveSharedMatchForRecipient (dismissal) failed:", e);
    return false;
  }
  try {
    await deleteDoc(doc(db, "sharedGrants", grantId));
  } catch (e) {
    console.warn("fsRemoveSharedMatchForRecipient (grant cleanup) failed:", e?.code ?? e);
  }
  return true;
}

export async function fsFetchSessionById(id) {
  const safeDocId = safeId(id);

  // Try primary read (own match or permissive security rules).
  try {
    const metaSnap = await getDoc(doc(db, "matches", safeDocId));
    if (metaSnap.exists()) {
      const meta = matchFromDoc(metaSnap);
      const dataSnap = await getDoc(doc(db, "matchData", safeDocId));
      meta.stats = dataSnap.exists() ? dataSnap.data().buffer ?? "" : "";
      // Surface the extra live-coding state (match squad + substitution pairs)
      // written alongside the buffer so a resumed match restores them.
      if (dataSnap.exists() && dataSnap.data().subsState) {
        try {
          const subs = JSON.parse(dataSnap.data().subsState);
          meta.matchSquadA = subs.matchSquadA ?? null;
          meta.matchSquadB = subs.matchSquadB ?? null;
          meta.subPairs = subs.subPairs ?? null;
        } catch {
          /* ignore malformed subsState */
        }
      }
      // Saved Catapult Focus jump data (if a file was ever loaded for this
      // match) -- expanded here so it's ready to use as soon as the match
      // opens (see CatapultReport.jsx).
      if (dataSnap.exists() && dataSnap.data().catapultFocusData) {
        const cf = decompressCatapultFocusData(dataSnap.data().catapultFocusData);
        if (cf) meta.catapultFocusData = cf;
      }
      return meta;
    }
  } catch (e) {
    console.warn("fsFetchSessionById primary read failed:", e?.code ?? e);
  }

  // Fallback: read from sharedGrants (for matches shared with the current user).
  const email = currentEmail();
  if (email) {
    try {
      const grantId = `${safeDocId}_${safeEmailId(email)}`;
      const grantSnap = await getDoc(doc(db, "sharedGrants", grantId));
      if (grantSnap.exists()) {
        const g = grantSnap.data();
        return {
          ...matchFromGrant(g, safeDocId),
          stats: g.stats ?? "",
        };
      }
    } catch (e) {
      console.warn("fsFetchSessionById sharedGrant fallback failed:", e?.code ?? e);
    }
  }

  return null;
}

// Lightweight, Catapult-only read.  Session.jsx's `getLatest()` (the "open a
// match directly by id, no router state" path) fetches match stats from the
// legacy REST API (`getSession()` in VBLiveAPIAction.js), which has no
// concept of `catapultFocusData` at all -- it's a Firestore-only field
// written by `fsStoreSession`. Without this, a migrated user's previously
// loaded Catapult Focus CSV data silently "disappears" every time the match
// is reopened via that path, even though it's still sitting in Firestore.
export async function fsFetchCatapultFocusData(id) {
  try {
    const dataSnap = await getDoc(doc(db, "matchData", safeId(id)));
    if (dataSnap.exists() && dataSnap.data().catapultFocusData) {
      return decompressCatapultFocusData(dataSnap.data().catapultFocusData);
    }
  } catch (e) {
    console.warn("fsFetchCatapultFocusData failed:", e?.code ?? e);
  }
  return null;
}

export async function fsFetchTeamByNameAndSessionId(name /*, sessionId */) {
  const uid = currentUid();
  if (!uid) return null;
  const snap = await getDoc(doc(db, "teams", `${uid}_${slugify(name)}`));
  if (!snap.exists()) return null;
  const d = snap.data();
  return {
    name: d.name ?? name,
    players: parseMaybeJSON(d.players) ?? [],
    lineup: parseMaybeJSON(d.lineup) ?? [],
    isOppPassing: d.isOppPassing === true,
    isMBFollowingSetter: d.isMBFollowingSetter === true,
    logoURL: d.logoURL ?? "",
    colour: d.colour ?? null,
  };
}

// ---- Writes ---------------------------------------------------------------

export async function fsStoreSession(match) {
  const uid = currentUid();
  const email = currentEmail();
  if (!uid) return 0;

  // Doc id: reuse the match's stable guid so re-saving overwrites in place;
  // otherwise mint a fresh id.
  const id = safeId(match.guid || doc(collection(db, "matches")).id);

  let seconds = 0;
  try {
    seconds = match.TrainingDate.getTime() / 1000;
  } catch {
    seconds = new Date(match.TrainingDate).getTime() / 1000;
  }
  let dt;
  try {
    dt = new Date(seconds * 1000);
  } catch {
    dt = new Date();
  }

  let scores = "";
  for (const game of match.sets ?? []) {
    if (scores.length > 0) scores += ", ";
    scores += `${game.HomeScore}-${game.AwayScore}`;
  }

  // Origin: explicit (e.g. "New" from the in-app coder) or detected from the
  // buffer format.
  const isPSVB =
    typeof match.buffer === "string" && match.buffer.includes("PSVB");
  const origin = match.origin || (isPSVB ? "PSVB" : "DVW");

  await setDoc(doc(db, "matches", id), {
    legacyId: id,
    teamA: match.teamA?.Name ?? "",
    teamB: match.teamB?.Name ?? "",
    description: `${match.teamA?.Name ?? ""} vs ${match.teamB?.Name ?? ""}`,
    tournament: match.tournamentName || "?",
    scores,
    origin,
    sessionDateTimeInSeconds: seconds,
    sessionDateString: dt.toLocaleDateString(),
    videoOnlineUrl: match.videoOnlineUrl ?? null,
    videoStartTimeSeconds: match.videoStartTimeSeconds ?? -1,
    videoOffset: match.videoOffset ?? -1,
    shareStatus: match.shareStatus ?? null,
    shareUsers: match.shareUsers ?? null,
    appName: "VBLive",
    // "Points to close a set" override chosen on import
    // (Input.jsx/Session.jsx) — re-applied on every re-parse of the saved
    // buffer so the sets-won match score stays consistent after a reload.
    winScore: match.winScore ?? null,
    winByTwo: match.winByTwo ?? null,
    owner: uid,
    ownerEmail: email,
    updatedAt: serverTimestamp(),
  });

  // Heavy compressed buffer in its own document (fetched on demand).
  // merge:true + conditionally omitting catapultFocusData below: most save
  // paths (DVMatchCoding's Save, VideoAnalysis's Save to database, live
  // auto-save, etc.) build their OWN match object straight from the buffer
  // and never load/copy catapultFocusData onto it at all. A plain (non-merge)
  // setDoc here would then write `catapultFocusData: null` on every one of
  // those saves, silently wiping out jump data the Catapult tab had saved
  // moments earlier — the exact "I have to reload the Focus file every time"
  // bug this guards against.
  const matchDataPayload = {
    buffer: myzip(match.buffer),
    codec: "legacy",
    // Extra live-coding state the DVW scout buffer can't represent — the chosen
    // match squad and substitution-pair tracking — serialised so a resume from
    // the database restores the sub popover's candidate list + sub history
    // correctly (see DVMatchCoding's load effect).
    subsState: JSON.stringify({
      matchSquadA: match.matchSquadA ?? null,
      matchSquadB: match.matchSquadB ?? null,
      subPairs: match.subPairs ?? null,
    }),
    owner: uid,
    ownerEmail: email,
    updatedAt: serverTimestamp(),
  };
  // Catapult Focus jump data (periods + jumps), if a CSV was loaded for this
  // match -- compressed the same way as the main buffer. Only included when
  // present on the in-memory match so an unrelated save doesn't clobber it.
  if (match.catapultFocusData) {
    matchDataPayload.catapultFocusData = compressCatapultFocusData(match.catapultFocusData);
  }
  await setDoc(doc(db, "matchData", id), matchDataPayload, { merge: true });

  // Best-effort: refresh the teams/players records from this match, in the
  // BACKGROUND (not awaited). This re-parses the whole match buffer and
  // writes one Firestore doc per team + per player, which gets slower as a
  // match/coding session grows — awaiting it here made every "Save" (manual
  // button in DVMatchCoding, both Draw & Code and DV-code modes, plus the
  // one-shot auto-save on match end) block on that full re-parse + a chain of
  // sequential doc writes. The actual save — the two setDoc calls above — is
  // already durable by the time we return; teams/players are derived/
  // auxiliary data, so their refresh no longer holds up the save UI. Errors
  // are swallowed here since nothing awaits this promise.
  upsertTeamsAndPlayers(match, { uid, email, id, seconds, origin, isPSVB }).catch((e) => {
    console.log("Failed to upsert teams/players for match", id, e);
  });

  return id;
}

// Parse a saved match's buffer and upsert its teams + players. Player team
// references are merged (arrayUnion) so a player keeps the teams from other
// matches.
async function upsertTeamsAndPlayers(match, ctx) {
  const { uid, email, id, seconds, origin, isPSVB } = ctx;
  if (typeof match.buffer !== "string" || match.buffer.length === 0) return;
  let parsed = null;
  try {
    parsed = isPSVB
      ? initWithPSVBCompressedBuffer(myzip(match.buffer))
      : generateMatch(match.buffer);
  } catch (e) {
    console.log("Could not parse match buffer for teams/players", e);
    return;
  }
  if (!parsed) return;

  const teamsMap = new Map();
  const playersMap = new Map();
  collectTeamsAndPlayers(
    parsed,
    { uid, email, origin, sessionId: id, seconds },
    teamsMap,
    playersMap
  );

  await Promise.all(
    [...teamsMap.values()].map((team) =>
      setDoc(doc(db, "teams", team.id), {
        name: team.name,
        nameLower: team.nameLower,
        rosterHash: team.rosterHash,
        playerCount: team.playerCount,
        players: team.players,
        playerIds: team.playerIds,
        origin: team.origin ?? null,
        lastSessionId: team.lastSessionId ?? null,
        lastSeenSeconds: team.lastSeenSeconds ?? null,
        owner: uid,
        ownerEmail: email,
        updatedAt: serverTimestamp(),
      })
    )
  );

  await Promise.all(
    [...playersMap.values()].map((p) =>
      setDoc(
        doc(db, "players", p.id),
        {
          firstName: p.firstName,
          lastName: p.lastName,
          nickName: p.nickName,
          nameLower: p.nameLower,
          positions: p.positions ?? [],
          teamIds: arrayUnion(...p.teamIds),
          teamNames: arrayUnion(...p.teamNames),
          owner: uid,
          ownerEmail: email,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      )
    )
  );
}

export async function fsDeleteMatch(match) {
  const id = safeId(match.id);
  try {
    await deleteDoc(doc(db, "matches", id));
    await deleteDoc(doc(db, "matchData", id));
    return true;
  } catch (e) {
    console.log("Failed to delete match from Firestore", e);
    return false;
  }
}

// Delete all `teams` docs with the given name owned by the current user, then
// delete any `players` whose teamNames list contains ONLY that team name.
export async function fsDeleteOrphanedTeam(teamName) {
  const uid = currentUid();
  if (!uid || !teamName) return;
  try {
    const tSnap = await getDocs(
      query(
        collection(db, "teams"),
        where("owner", "==", uid),
        where("name", "==", teamName)
      )
    );
    for (const d of tSnap.docs) {
      await deleteDoc(d.ref);
    }
    const pSnap = await getDocs(
      query(collection(db, "players"), where("owner", "==", uid))
    );
    for (const d of pSnap.docs) {
      const teamNames = d.data().teamNames ?? [];
      if (teamNames.length === 1 && teamNames[0] === teamName) {
        await deleteDoc(d.ref);
      }
    }
  } catch (e) {
    console.warn("fsDeleteOrphanedTeam failed:", e);
  }
}

export async function fsStoreTeam(team) {
  const uid = currentUid();
  const email = currentEmail();
  if (!uid) return;
  // Build the payload; omit players/lineup entirely when the caller doesn't
  // have them (rather than writing `null`) so a merge-write never wipes a
  // previously-stored roster/lineup out from under a caller that only meant
  // to update e.g. the colour.
  const payload = {
    name: team.name ?? "",
    isOppPassing: team.isOppPassing === true,
    isMBFollowingSetter: team.isMBFollowingSetter === true,
    logoURL: team.logoURL ?? "",
    colour: team.colour ?? null,
    owner: uid,
    ownerEmail: email,
    updatedAt: serverTimestamp(),
  };
  if (team.players && team.players.length > 0) {
    payload.players = JSON.stringify(team.players);
  }
  if (team.lineup && team.lineup.length > 0) {
    payload.lineup = JSON.stringify(team.lineup);
  }
  // `merge: true` so this never fully replaces the document \u2014 only the
  // fields above are touched, protecting any other stored fields.
  await setDoc(doc(db, "teams", `${uid}_${slugify(team.name)}`), payload, {
    merge: true,
  });
}

// Convert shareUsers to a clean array of lowercase email strings regardless of
// whether it arrives as a comma-separated string, an array, or null.
function normalizeShareUsers(value) {
  if (!value) return null;
  const raw = Array.isArray(value)
    ? value
    : String(value).split(",");
  const arr = raw.map((s) => String(s).trim().toLowerCase()).filter(Boolean);
  return arr.length > 0 ? arr : null;
}

export async function fsShareSession(match) {
  const uid = currentUid();
  const id = safeId(match.id);
  if (!uid) {
    // The only remaining silent failure path here — surfaced so a falsy
    // return from shareSession isn't a dead end to diagnose next time.
    console.warn("fsShareSession: no authenticated uid, cannot share match", match.id);
    return false;
  }

  const shareUsersList = normalizeShareUsers(match.shareUsers);

  try {
    // 1. Update the matches document.
    await updateDoc(doc(db, "matches", id), {
      shareStatus: match.shareStatus ?? null,
      shareUsers: shareUsersList,
      // So a recipient who previously dismissed this match (see
      // fsRemoveSharedMatchForRecipient) sees it again after a fresh share --
      // compared against sharedMatchDismissals.dismissedAt on read.
      lastSharedAt: serverTimestamp(),
    });

    // 2. Delete any existing sharedGrants written by this owner for this match.
    try {
      const existingSnap = await getDocs(
        query(
          collection(db, "sharedGrants"),
          where("matchId", "==", id),
          where("ownerUid", "==", uid)
        )
      );
      for (const grantDoc of existingSnap.docs) {
        await deleteDoc(grantDoc.ref);
      }
    } catch (e) {
      console.warn("fsShareSession: could not clean up old grants:", e?.code ?? e);
    }

    // 3. Write new sharedGrants when sharing with specific users (status === 2).
    if (match.shareStatus === 2 && shareUsersList && shareUsersList.length > 0) {
      // Read the compressed match buffer so recipients can open the match.
      let stats = "";
      try {
        const dataSnap = await getDoc(doc(db, "matchData", id));
        stats = dataSnap.exists() ? dataSnap.data().buffer ?? "" : "";
      } catch (e) {
        console.warn("fsShareSession: could not read matchData:", e?.code ?? e);
      }

      const meta = grantMeta(match);
      for (const email of shareUsersList) {
        const grantId = `${id}_${safeEmailId(email)}`;
        await setDoc(doc(db, "sharedGrants", grantId), {
          matchId: id,
          recipientEmail: email,
          ownerUid: uid,
          ownerEmail: currentEmail() ?? "",
          stats,
          ...meta,
          sharedAt: serverTimestamp(),
        });
      }
    }

    return true;
  } catch (e) {
    console.error("fsShareSession failed:", e);
    return false;
  }
}
